/**
 * externalMints.js — projects that are not minted from this site.
 *
 * The gating is a nice trick, and worth writing down because nothing in the
 * contract explains it. ImmutablesArt.sol line 869 reads:
 *
 *   require(!paused || msg.sender == projectIdToArtistAddress[_projectId]
 *           || isTeammember[msg.sender], "paused");
 *
 * The artist is exempt from the pause. So if the project's artist address is
 * set to a *contract* rather than a wallet, pausing the project closes minting
 * to everyone except that contract -- and the contract can impose whatever
 * condition it likes before minting on someone's behalf. Confirmed on chain:
 * Firmament's artist is a 3,099-byte contract, Way of the Worm's is 3,513
 * bytes, while ungated Syncret's is a plain wallet.
 *
 * The consequence here is that both projects read as paused, and the mint
 * modal rendered that as a greyed-out "minting currently paused" button. True,
 * and useless: not paused because it is finished, paused because minting
 * happens somewhere else, with nothing saying where.
 *
 * Each entry says where minting really happens and, where eligibility is
 * answerable on chain, how to answer it for the connected wallet.
 */

/**
 * The claim entry point Syncret holders call. Distinct from Firmament's
 * registered artist address (0x12320d5a...), which is the contract the pause
 * exempts; this is the one a person interacts with.
 */
export const SYNCRET_HOLDER_MINT = '0x3b730e7ee50cae2431222bbb120a8636d2f39b14';

export const SYNCRET_HOLDER_MINT_ABI = [
  { name: 'claimedEdition', type: 'function', stateMutability: 'view',
    inputs: [{ name: 'e', type: 'uint256' }], outputs: [{ name: '', type: 'bool' }] },
  { name: 'claimedToken', type: 'function', stateMutability: 'view',
    inputs: [{ name: 't', type: 'uint256' }], outputs: [{ name: '', type: 'bool' }] },
  // Confirmed against real claims on chain: every Firmament claim is
  // claimToken(uint256) with the ImmutablesArt token id of the Syncret.
  { name: 'claimToken', type: 'function', stateMutability: 'nonpayable',
    inputs: [{ name: 't', type: 'uint256' }], outputs: [] },
  { name: 'claimEdition', type: 'function', stateMutability: 'nonpayable',
    inputs: [{ name: 'e', type: 'uint256' }], outputs: [] },
];

/**
 * Way of the Worm mints from here -- not from the project's registered artist
 * address, which is a separate contract this one calls. Taken from the input
 * data of real mints rather than assumed.
 */
export const WORM_MINT = '0x54316F1A885c2c2A10bC440799d7D14fa3482106';

export const WORM_MINT_ABI = [
  { name: 'canMint', type: 'function', stateMutability: 'view',
    inputs: [{ name: 'a', type: 'address' }], outputs: [{ name: '', type: 'bool' }] },
  { name: 'mint', type: 'function', stateMutability: 'nonpayable', inputs: [], outputs: [] },
];

const EXTERNAL_MINTS = {
  // Firmament
  5: {
    title: 'Free to Syncret holders',
    summary:
      'Every Syncret carries one free Firmament claim. Connect a wallet holding a ' +
      'Syncret whose claim is unspent and make it here — the gating contract on ' +
      'Etherscan does the same thing for anyone who would rather make the call ' +
      'themselves.',
    mintUrl: 'https://etherscan.io/address/' + SYNCRET_HOLDER_MINT + '#writeContract',
    mintLabel: 'claim on Etherscan',
    // The claim can be made from here, so it is -- Etherscan stays as the
    // fallback for anyone who would rather see the call themselves.
    claim: { method: 'claimToken', argFrom: 'tokenId', verb: 'claim Firmament' },
    // Eligibility is answerable on chain, so the modal answers it.
    gate: {
      kind: 'syncretHolder',
      heldProjectId: 3,
      contract: SYNCRET_HOLDER_MINT,
      abi: SYNCRET_HOLDER_MINT_ABI,
    },
    // Only what summary does not already say. Both are rendered together --
    // in the mint panel and on a daily card -- and this used to open by
    // repeating the requirement verbatim.
    howToQualify:
      'Before buying a Syncret on the secondary market, check `claimedEdition` ' +
      'or `claimedToken` on the gating contract — a spent claim never comes back.',
    // No leading slash: "/#/3" is a path change and reloads the whole app,
    // "#/3" is a hash change the router handles in place. ProjectModal's
    // permalink uses the same form.
    qualifyUrl: '#/3',
    qualifyLabel: 'browse Syncret',
    // The registered artist of a gated project is a contract, not the person
    // who wrote the code, and the mint details showed that as a bare mismatch.
    // It is not one: the contract's owner() is the address that posted the
    // script. The panel checks that live and says so. Addresses verified on
    // chain; the poster is the sender of the script transaction.
    provenance: {
      artistContract: '0x12320d5ad4477fb210c8f5286ee517b316723b3f',
      codePoster: '0xfc3d126d801d5ce47df73b533d82917854641282',
    },
  },

  // Way of the Worm
  6: {
    title: 'Free to Worm Disciples',
    summary:
      'Way of the Worm is free to Worm Disciples. The mint contract decides who ' +
      'qualifies, and it will answer for the connected wallet.',
    // markegli.com/worm/ hosted this and now returns 404, so the mint contract
    // on Etherscan is the durable fallback rather than a dead link.
    mintUrl: 'https://etherscan.io/address/' + WORM_MINT + '#writeContract',
    mintLabel: 'mint on Etherscan',
    gate: {
      kind: 'allowlist',
      contract: WORM_MINT,
      abi: WORM_MINT_ABI,
      check: 'canMint',
    },
    claim: { method: 'mint', argFrom: null, verb: 'mint Way of the Worm' },
    howToQualify:
      'Eligibility is held by the mint contract. A wallet that has already used ' +
      'its claim reads as ineligible, as does one that was never a Disciple.',
    // Both the registered artist contract and the mint gate are owned by the
    // address that posted the code.
    provenance: {
      artistContract: '0x82b14438e77b12ac8ea527c7ed96908efa59e017',
      gateContract: WORM_MINT,
      codePoster: '0xfc3d126d801d5ce47df73b533d82917854641282',
    },
  },
};

/** Just owner(), for asking a gate or artist contract who holds it. */
export const OWNER_ABI = [
  { name: 'owner', type: 'function', stateMutability: 'view', inputs: [], outputs: [{ name: '', type: 'address' }] },
];

export const externalMintFor = (projectId) => EXTERNAL_MINTS[Number(projectId)] || null;

/**
 * The generic case, for a gated project nobody has written an entry for yet.
 *
 * The tell is already computed elsewhere in the app: the address that posted
 * the script to Immutables.co is the artist doing the work, and normally it is
 * also the project's artist address. When the two differ on a paused project,
 * the artist address has been pointed at something else -- which, given the
 * pause exemption above, is exactly how a mint gets gated.
 *
 * That is a strong enough signal to stop saying "paused" and weak enough that
 * it should not claim to know the rules. So it says where the rules are
 * written down instead of inventing them.
 */
export const GATED_UNKNOWN = {
  title: 'Minting is gated',
  summary:
    'This project is paused here because minting runs through another contract, ' +
    'which decides who may mint. This site does not know its rules.',
  // The same invite platformConfig and AboutPlatformModal use.
  discordUrl: 'https://discord.gg/A329NUvrAf',
  discordLabel: 'check Discord for mint instructions',
};

export default EXTERNAL_MINTS;
